import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { Header, Layout, NextButton, Overview } from '@eneco/flows/components';
import { useStepContent } from '@eneco/flows/hooks';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useMachine } from '@xstate/react';
import { useForm } from 'react-hook-form';

import { FlowContextToonCancellation } from '../../../context';
import { overviewMachine } from '../../machines/overviewMachine';

export const OTHER_OVERVIEW_VALUE = 'ANL';
interface FormData {
  overview: string;
  otherOverview?: string;
}
export const OverviewStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextToonCancellation>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [state, sendToOverviewMachine] = useMachine(overviewMachine);
  const { customerId, accountId, label, businessUnit } = useDC();
  const { trackFunnelCompleted } = useTracking();

  const { summary } = state.context;
  const reason = useFlowSelector(state => state.context.reason);
  const explanation = useFlowSelector(state => state.context.explanation);
  const intake = useFlowSelector(state => state.context.intake);
  const reasonLabel = useFlowSelector(state => state.context.reasonLabel);

  const accountIdFromIntake = intake?.accounts?.[0]?.accountId;

  const content = useStepContent('CancelationSummaryStep');

  // The flow completes below, and its router sends the visitor to the thank you page with the reason.
  useEffect(() => {
    if (state.matches('SUCCESS')) {
      trackFunnelCompleted({ funnel: 'TOON_CANCELATION' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    sendToOverviewMachine({
      type: 'FETCH_DATA',
      config: { businessUnit, label },
      values: {
        customerId: customerId,
        accountId: accountIdFromIntake || accountId,
      },
    });
  }, [accountId, accountIdFromIntake, businessUnit, customerId, label, sendToOverviewMachine]);

  useEffect(() => {
    if (!reason || reason.trim() === '') {
      sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_REASON' });
    }
  }, [reason, sendToFlowMachine]);

  const {
    watch,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = useForm<FormData>({
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      sendToOverviewMachine({
        type: 'SUBMIT',
        config: { businessUnit, label },
        values: {
          reason,
          explanation,
          customerId,
          accountId,
        },
      });
    }
  }, [accountId, businessUnit, customerId, explanation, isSubmitSuccessful, label, reason, sendToOverviewMachine]);

  useEffect(() => {
    if (state.matches('SUCCESS')) {
      sendToFlowMachine({ type: 'NEXT_PAGE' });
    }
  }, [sendToFlowMachine, state]);

  const selectedOverview = watch('overview');
  const otherOverview = watch('otherOverview');
  const isSubmitDisabled = selectedOverview === '' || (selectedOverview === OTHER_OVERVIEW_VALUE && !otherOverview);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit} isLoading={state.matches('FETCHING')}>
      <Header>
        <Header.Title>{content?.content_title}</Header.Title>
        {content?.text && <Header.Text>{content.text}</Header.Text>}
      </Header>

      <Overview>
        <Overview.Header>{content?.personalDetailsOverview_title}</Overview.Header>
        <Overview.Details type="address" title={<RichText html={content?.description} />}>
          <Text as="p">
            {summary?.data?.account?.deliveryAddress?.street} {summary?.data?.account?.deliveryAddress?.houseNumber}{' '}
            {summary?.data?.account?.deliveryAddress?.houseNumberSuffix}
          </Text>
          <Text as="p">
            {summary?.data?.account?.deliveryAddress?.postalCode} {summary?.data?.account?.deliveryAddress?.city}
          </Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{content?.cancelConfirmAddressLabel}</Overview.Header>
        <Overview.Details type="profile" title={content?.cancelConfirmAddressLabel ?? ''}>
          <Text as="p">{intake?.confirmationEmailAddress}</Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{content?.cancelationOverview_title}</Overview.Header>
        <Overview.Details type="situation" title={content?.cancelationProductLabel ?? ''}>
          <Text as="p"> {content?.cancelationProductName}</Text>
        </Overview.Details>

        <Overview.Details type="situation" title={content?.cancelationReasonLabel ?? ''}>
          <Text as="p">
            {reasonLabel}
            {explanation ? `- ${explanation}` : ''}
          </Text>
        </Overview.Details>
      </Overview>

      <Stack.Item>
        <Text size="BodyS">{content?.disclaimerText}</Text>
      </Stack.Item>
      <Stack.Item>
        <NextButton alignX="end" isLoading={isSubmitDisabled}>
          {content?.nextStepText}
        </NextButton>
      </Stack.Item>
    </Layout.Content>
  );
};
